/**
 * CompanySidebar.jsx - Låst sidebar for B2B-kunder
 * 
 * OPPDATERT v2.0:
 * - ADMIN-badge vises for organisasjons-administratorer
 * - Moduler er nå en dropdown med alle aktive moduler
 * - Bunnseksjon viser: Bedriftsnavn, Brukernavn, Rolle, Logg ut
 * - Visuell differensiering mellom admin og bruker
 * 
 * Bruker useAuth() og useCompany() for bruker- og bedriftsdata.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { 
  LayoutDashboard, 
  FileText, 
  Layers, 
  Users, 
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  Briefcase,
  Home,
  ShoppingBag,
  Shield,
  GraduationCap,
  Building2,
  Crown,
  Baby,
  Heart,
  Key,
  CreditCard,
  FileSignature,
  Clock,
  Calendar,
  X,
  FolderOpen,
  UserPlus,
  UserMinus,
  FilePlus,
  FileEdit,
  Eye,
  Trash2,
  RefreshCw
} from 'lucide-react';

// Ikon-mapping for moduler
const MODULE_ICONS = {
  'arbeidsrett-bedrift': Briefcase,
  arbeidsrett: Briefcase,
  husleierett: Home,
  'husleierett-naering': Building2,
  forbrukerrett: ShoppingBag,
  barnevern: Shield,
  'barnevern-master': Heart,
  skole: GraduationCap,
  barnehage: Baby,
  gdpr: Shield,
  kontraktsbibliotek: FileSignature,
  default: Layers
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ikon og farge per aktivitetstype
const ACTIVITY_CONFIG = {
  document_created:  { icon: FilePlus,  color: '#22c55e', label: 'Dokument' },
  document_edited:   { icon: FileEdit,  color: '#3b82f6', label: 'Dokument' },
  module_opened:     { icon: Eye,       color: '#B87333', label: 'Modul' },
  template_created:  { icon: FileText,  color: '#a855f7', label: 'Mal' },
  template_used:     { icon: FolderOpen,color: '#6366f1', label: 'Mal' },
  user_added:        { icon: UserPlus,  color: '#22c55e', label: 'Bruker' },
  user_removed:      { icon: UserMinus, color: '#ef4444', label: 'Bruker' },
  settings_changed:  { icon: Settings,  color: '#f59e0b', label: 'Innst.' },
  template_deleted:  { icon: Trash2,    color: '#ef4444', label: 'Mal' },
  default:           { icon: Clock,     color: '#8899A8', label: '' },
};

// Relativ tidsangivelse på norsk
function relativeTime(isoString) {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diff = Math.max(0, now - then);

  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'Nå nettopp';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minutt' : 'minutter'} siden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'time' : 'timer'} siden`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'I går';
  if (days < 7) return `${days} dager siden`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} ${weeks === 1 ? 'uke' : 'uker'} siden`;
  const months = Math.floor(days / 30);
  return `${months} ${months === 1 ? 'måned' : 'måneder'} siden`;
}

export default function CompanySidebar() {
  const location = useLocation();
  const { user, logout, token } = useAuth();
  const { company, activeModules, userRole, isAdmin } = useCompany();
  
  const [modulesOpen, setModulesOpen] = useState(true);
  const [activityOpen, setActivityOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const activityBtnRef = useRef(null);
  const activityPopupRef = useRef(null);

  // Hent aktiviteter fra API når popup åpnes
  useEffect(() => {
    if (!activityOpen || !token) return;
    let cancelled = false;
    setActivitiesLoading(true);
    axios.get(`${API}/activity?limit=10`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      if (!cancelled) setActivities(res.data?.data || []);
    })
    .catch(() => {
      if (!cancelled) setActivities([]);
    })
    .finally(() => {
      if (!cancelled) setActivitiesLoading(false);
    });
    return () => { cancelled = true; };
  }, [activityOpen, token]);

  // Logg modul-åpning ved navigering (maks én gang per modul per økt)
  const loggedModulesRef = useRef(new Set());
  useEffect(() => {
    const match = location.pathname.match(/^\/company\/modules\/([^/]+)$/);
    if (!match || !token) return;
    const moduleKey = match[1];
    if (loggedModulesRef.current.has(moduleKey)) return;
    loggedModulesRef.current.add(moduleKey);
    axios.post(`${API}/activity`, {
      action: 'module_opened',
      title: `Åpnet ${moduleKey}-modulen`,
      module: moduleKey,
    }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  }, [location.pathname, token]);

  // Lukk popup ved klikk utenfor
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        activityBtnRef.current && !activityBtnRef.current.contains(e.target) &&
        activityPopupRef.current && !activityPopupRef.current.contains(e.target)
      ) {
        setActivityOpen(false);
      }
    };
    if (activityOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activityOpen]);

  // Sjekk om en sti er aktiv
  const isActive = (path) => {
    if (path === '/company/dashboard') {
      return location.pathname === '/company/dashboard' || location.pathname === '/company';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Sjekk om en modul er aktiv
  const isModuleActive = (moduleKey) => {
    return location.pathname === `/company/modules/${moduleKey}`;
  };

  // Sjekk om vi er i modul-seksjonen
  const isInModulesSection = location.pathname.startsWith('/company/modules');

  // Hent ikon for en modul
  const getModuleIcon = (moduleKey) => {
    return MODULE_ICONS[moduleKey] || MODULE_ICONS.default;
  };

  // Rolletekst
  const getRoleLabel = () => {
    return userRole === 'company_admin' ? 'Administrator' : 'Bruker';
  };

  return (
    <aside 
      className="fixed inset-y-0 left-0 z-40 w-64 bg-[#1B2631] border-r border-[#2C3E50] flex flex-col"
      data-testid="company-sidebar"
    >
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* HEADER - Logo og bedriftsnavn */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="h-16 flex items-center px-4 border-b border-[#2C3E50]">
        <Link to="/company/dashboard" className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_juridisk-motor-no/artifacts/1x4dyzgp_image.jpg" 
            alt="RettVerk" 
            className="w-9 h-9 rounded-lg object-contain"
            data-testid="company-logo"
          />
          <div>
            <p className="text-sm font-semibold text-white">RettVerk</p>
            <p className="text-xs text-[#B87333]">Bedriftsportal</p>
          </div>
        </Link>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* NAVIGASJON */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        
        {/* Dashboard */}
        <Link
          to="/company/dashboard"
          data-testid="sidebar-link-dashboard"
          className={`
            flex items-center gap-3 px-3 py-2.5 rounded-lg
            transition-all duration-200
            ${isActive('/company/dashboard') 
              ? 'bg-[#B87333]/20 text-[#B87333]' 
              : 'text-[#8899A8] hover:bg-[#2C3E50] hover:text-white'
            }
          `}
        >
          <LayoutDashboard className={`w-5 h-5 flex-shrink-0 ${isActive('/company/dashboard') ? 'text-[#B87333]' : ''}`} />
          <span className="text-sm font-medium">Dashboard</span>
        </Link>

        {/* Dokumenter */}
        <Link
          to="/company/documents"
          data-testid="sidebar-link-documents"
          className={`
            flex items-center gap-3 px-3 py-2.5 rounded-lg
            transition-all duration-200
            ${isActive('/company/documents') 
              ? 'bg-[#B87333]/20 text-[#B87333]' 
              : 'text-[#8899A8] hover:bg-[#2C3E50] hover:text-white'
            }
          `}
        >
          <FileText className={`w-5 h-5 flex-shrink-0 ${isActive('/company/documents') ? 'text-[#B87333]' : ''}`} />
          <span className="text-sm font-medium">Dokumenter</span>
        </Link>


        {/* Separator */}
        <div className="my-2 border-t border-[#2C3E50]/50" />

        {/* Brukere (kun for admin) */}
        {isAdmin && (
          <Link
            to="/company/users"
            data-testid="sidebar-link-users"
            className={`
              flex items-center gap-3 px-3 py-2.5 rounded-lg
              transition-all duration-200
              ${isActive('/company/users') 
                ? 'bg-[#B87333]/20 text-[#B87333]' 
                : 'text-[#8899A8] hover:bg-[#2C3E50] hover:text-white'
              }
            `}
          >
            <Users className={`w-5 h-5 flex-shrink-0 ${isActive('/company/users') ? 'text-[#B87333]' : ''}`} />
            <span className="text-sm font-medium">Brukere</span>
          </Link>
        )}

        {/* Innstillinger */}
        <Link
          to="/company/settings"
          data-testid="sidebar-link-settings"
          className={`
            flex items-center gap-3 px-3 py-2.5 rounded-lg
            transition-all duration-200
            ${isActive('/company/settings') 
              ? 'bg-[#B87333]/20 text-[#B87333]' 
              : 'text-[#8899A8] hover:bg-[#2C3E50] hover:text-white'
            }
          `}
        >
          <Settings className={`w-5 h-5 flex-shrink-0 ${isActive('/company/settings') ? 'text-[#B87333]' : ''}`} />
          <span className="text-sm font-medium">Innstillinger</span>
        </Link>

        {/* Fakturering (kun for admin) */}
        {isAdmin && (
          <Link
            to="/company/billing"
            data-testid="sidebar-link-billing"
            className={`
              flex items-center gap-3 px-3 py-2.5 rounded-lg
              transition-all duration-200
              ${isActive('/company/billing') 
                ? 'bg-[#B87333]/20 text-[#B87333]' 
                : 'text-[#8899A8] hover:bg-[#2C3E50] hover:text-white'
              }
            `}
          >
            <CreditCard className={`w-5 h-5 flex-shrink-0 ${isActive('/company/billing') ? 'text-[#B87333]' : ''}`} />
            <span className="text-sm font-medium">Fakturering</span>
          </Link>
        )}

        {/* Nylig aktivitet - trigger-knapp */}
        <div ref={activityBtnRef}>
          <button
            onClick={() => setActivityOpen(!activityOpen)}
            data-testid="sidebar-recent-activity-btn"
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
              transition-all duration-200
              ${activityOpen 
                ? 'bg-[#B87333]/20 text-[#B87333]' 
                : 'text-[#8899A8] hover:bg-[#2C3E50] hover:text-white'
              }
            `}
          >
            <Clock className={`w-5 h-5 flex-shrink-0 ${activityOpen ? 'text-[#B87333]' : ''}`} />
            <span className="text-sm font-medium">Nylig aktivitet</span>
          </button>
        </div>
      </nav>

      {/* Nylig aktivitet popup — utenfor nav for å unngå overflow-clip */}
      {activityOpen && (
        <div 
          ref={activityPopupRef}
          className="fixed left-64 bottom-32 ml-2 w-80 bg-[#1B2631] border border-[#2C3E50] rounded-xl shadow-2xl z-50 overflow-hidden"
          data-testid="recent-activity-popup"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2C3E50]">
            <span className="text-sm font-semibold text-white">Nylig aktivitet</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setActivityOpen(false); setTimeout(() => setActivityOpen(true), 50); }}
                className="p-1 rounded hover:bg-[#2C3E50] text-[#8899A8]"
                title="Oppdater"
                data-testid="activity-refresh-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setActivityOpen(false)} className="p-1 rounded hover:bg-[#2C3E50] text-[#8899A8]">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="p-2 space-y-1 max-h-72 overflow-y-auto">
            {activitiesLoading ? (
              <div className="flex items-center justify-center py-6">
                <div className="w-5 h-5 border-2 border-[#B87333] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-6 px-4">
                <Clock className="w-6 h-6 text-[#2C3E50] mx-auto mb-2" />
                <p className="text-xs text-[#8899A8]">Ingen aktivitet registrert ennå</p>
                <p className="text-[10px] text-[#8899A8]/60 mt-1">Aktiviteter logges automatisk når du bruker portalen</p>
              </div>
            ) : (
              activities.map((item) => {
                const cfg = ACTIVITY_CONFIG[item.action] || ACTIVITY_CONFIG.default;
                const Icon = cfg.icon;
                return (
                  <div key={item.id} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-[#0F1419] transition-colors" data-testid={`activity-item-${item.id}`}>
                    <div className="p-1.5 rounded-lg mt-0.5" style={{ backgroundColor: `${cfg.color}15` }}>
                      <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-white leading-snug">{item.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-[#8899A8]">{relativeTime(item.created_at)}</span>
                        {item.user_name && (
                          <>
                            <span className="text-[10px] text-[#2C3E50]">·</span>
                            <span className="text-[10px] text-[#8899A8] truncate">{item.user_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* BUNNSEKSJON - Bedrift og brukerinfo */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="p-4 border-t border-[#2C3E50] space-y-3">
        
        {/* Admin-snarveier (kun for admin) — over bedriftsinfo */}
        {isAdmin && (
          <div className="space-y-1">
            <Link
              to="/company/users"
              data-testid="sidebar-admin-manage-users"
              className="flex items-center gap-2 px-3 py-2 text-xs text-[#8899A8] hover:text-[#B87333] hover:bg-[#B87333]/10 rounded-lg transition-colors"
            >
              <Users className="w-3.5 h-3.5" />
              <span>Administrer brukere</span>
            </Link>
            <Link
              to="/company/settings"
              data-testid="sidebar-admin-security"
              className="flex items-center gap-2 px-3 py-2 text-xs text-[#8899A8] hover:text-[#B87333] hover:bg-[#B87333]/10 rounded-lg transition-colors"
            >
              <Key className="w-3.5 h-3.5" />
              <span>Sikkerhet og nøkler</span>
            </Link>
          </div>
        )}

        {/* Bedriftsinfo */}
        <div className="px-3 py-3 bg-[#0F1419] rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-[#B87333]/20 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-4 h-4 text-[#B87333]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate" data-testid="company-name">
                {company?.name || 'Min Bedrift'}
              </p>
            </div>
          </div>
          
          {/* Brukerinfo med ADMIN-badge */}
          <div className="flex items-center gap-2 pt-2 border-t border-[#2C3E50]/50">
            <div className="w-6 h-6 rounded-full bg-[#2C3E50] flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-medium text-[#B87333]">
                {user?.name?.charAt(0)?.toUpperCase() || 'B'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white truncate" data-testid="user-name">
                {user?.name || 'Bruker'}
              </p>
            </div>
            {isAdmin ? (
              <span 
                className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-[#B87333] text-white font-bold"
                data-testid="admin-badge"
              >
                <Crown className="w-3 h-3" />
                ADMIN
              </span>
            ) : (
              <span 
                className="text-[10px] px-1.5 py-0.5 rounded bg-[#2C3E50] text-[#8899A8]"
                data-testid="user-role"
              >
                {getRoleLabel()}
              </span>
            )}
          </div>
        </div>

        {/* Logg ut */}
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="flex items-center justify-center gap-2 px-3 py-2.5 w-full
            text-sm text-[#8899A8] hover:text-red-400
            bg-[#0F1419] hover:bg-red-500/10 
            rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span>Logg ut</span>
        </button>
      </div>
    </aside>
  );
}
